import { useEffect, useMemo, useRef, useState } from 'react'
import { useToastStore } from '@/stores/toastStore'
import {
  X,
  LayoutTemplate,
  Sparkles,
  Loader2,
  Repeat,
  Bell,
  Paperclip,
  Plus,
  FileText,
  Image as ImageIcon,
  MoreVertical,
  Check,
  Copy,
  Trash2,
  FolderKanban,
  Users,
  Mic,
  MicOff,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Button } from '@/components/ui/Button'
import { DatePicker } from '@/components/ui/DatePicker'
import { DatePickerBottomSheet } from '@/components/ui/DatePickerBottomSheet'
import { TemplatePickerModal } from './TemplatePickerModal'
import { useTaskStore } from '@/stores/taskStore'
import { useCategoryStore } from '@/stores/categoryStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useAttachmentStore } from '@/stores/attachmentStore'
import { useGroupStore } from '@/stores/groupStore'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import { useAuthStore } from '@/stores/authStore'
import { parseTaskWithAI, decomposeTaskWithAI, getAIConfig } from '@/services/aiService'
import { parseNaturalLanguage } from '@/lib/nlParser'
import { RescheduleNudgeModal } from './RescheduleNudgeModal'
import { MemoLinkModal } from './MemoLinkModal'
import { useFocusStore } from '@/stores/focusStore'
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition'
import { useKeyboardSafeBottom } from '@/hooks/useKeyboardSafeBottom'
import { NlHintChips } from './NlHintChips'
import { getCategoryColorStyle } from '@/lib/categoryColors'
import type { Task, TaskPriority, TaskAlarm, RepeatType, Attachment } from '@/lib/types'

interface TaskCreateModalProps {
  isOpen: boolean
  onClose: () => void
  defaultDueDate?: string
  /** 일괄 작업 생성: 여러 날짜에 동일한 작업을 각각 생성 (생성 모드 전용) */
  defaultDueDates?: string[]
  editTask?: Task | null
}

const priorityOptions: { value: TaskPriority; label: string }[] = [
  { value: 'none', label: '없음' },
  { value: 'low', label: '낮음' },
  { value: 'medium', label: '중간' },
  { value: 'high', label: '높음' },
]

const repeatOptions: { value: RepeatType; label: string }[] = [
  { value: 'daily', label: '매일' },
  { value: 'weekly', label: '주간' },
  { value: 'monthly', label: '월간' },
  { value: 'yearly', label: '매년' },
]

const alarmBeforeOptions: { value: number; label: string }[] = [
  { value: 0, label: '마감 시간에' },
  { value: 5, label: '5분 전' },
  { value: 15, label: '15분 전' },
  { value: 30, label: '30분 전' },
  { value: 60, label: '1시간 전' },
  { value: 1440, label: '1일 전' },
]

interface PendingFile {
  id: string
  file: File
  previewUrl?: string
}

// M3: Title max length
const TITLE_MAX_LENGTH = 200

// M3: Allowed file extensions
const ALLOWED_EXTENSIONS = new Set([
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.heic',
  '.pdf', '.txt', '.csv', '.md',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.zip', '.json',
])

function isAllowedFile(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase()
  return ALLOWED_EXTENSIONS.has(ext)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

function ExistingAttachmentItem({ attachment, onDelete }: {
  attachment: Attachment; onDelete: () => void
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (attachment.thumbnailData) {
      const url = URL.createObjectURL(attachment.thumbnailData)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    } else if (attachment.mimeType.startsWith('image/')) {
      const url = URL.createObjectURL(attachment.data)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    }
  }, [attachment.id, attachment.thumbnailData, attachment.mimeType])

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-surface-secondary group">
      {previewUrl ? (
        <img src={previewUrl} alt={attachment.filename} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
          {attachment.mimeType.startsWith('image/') ? (
            <ImageIcon className="w-4 h-4 text-disabled" />
          ) : (
            <FileText className="w-4 h-4 text-disabled" />
          )}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-heading truncate">{attachment.filename}</p>
        <p className="text-meta text-disabled">{formatFileSize(attachment.size)}</p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="p-1 rounded text-disabled hover:text-red-500 hover:bg-[var(--active-bg)] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

const EMPTY_ATTACHMENTS: Attachment[] = []

export function TaskCreateModal({ isOpen, onClose, defaultDueDate, defaultDueDates, editTask }: TaskCreateModalProps) {
  const addTask = useTaskStore((s) => s.addTask)
  const updateTask = useTaskStore((s) => s.updateTask)
  const toggleComplete = useTaskStore((s) => s.toggleComplete)
  const deleteTask = useTaskStore((s) => s.deleteTask)
  const categories = useCategoryStore((s) => s.categories)
  const aiEnabled = useSettingsStore((s) => s.settings.aiEnabled)
  const addAttachment = useAttachmentStore((s) => s.addAttachment)
  const loadAttachments = useAttachmentStore((s) => s.loadAttachments)
  const existingAttachments = useAttachmentStore((s) =>
    editTask?.id ? (s.attachments[editTask.id] || EMPTY_ATTACHMENTS) : EMPTY_ATTACHMENTS
  )
  const deleteExistingAttachment = useAttachmentStore((s) => s.deleteAttachment)
  const groups = useGroupStore((s) => s.groups)
  const addTaskToGroup = useGroupStore((s) => s.addTaskToGroup)
  const removeTaskFromGroup = useGroupStore((s) => s.removeTaskFromGroup)
  const sharedGroups = useSharedGroupStore((s) => s.sharedGroups)
  const addSharedTask = useSharedGroupStore((s) => s.addTask)
  const isLoggedIn = useAuthStore((s) => !!s.user)
  const voiceEnabled = useSettingsStore((s) => s.settings.voiceInputEnabled)
  const sttLanguage = useSettingsStore((s) => s.settings.ai?.language || 'ko')
  const { transcript, isListening, isSupported, startListening, stopListening } = useSpeechRecognition(sttLanguage)
  // 모바일 키보드가 열리면 고정 푸터(저장 CTA)를 키보드 위로 밀어 올림
  const kbOffset = useKeyboardSafeBottom()

  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState(false)
  const [memo, setMemo] = useState('')
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [dueDate, setDueDate] = useState<string>(defaultDueDate ?? '')
  const [dueTime, setDueTime] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('none')
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches)
  const [isAiParsing, setIsAiParsing] = useState(false)
  const [isAiDecomposing, setIsAiDecomposing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Repeat
  const [repeatEnabled, setRepeatEnabled] = useState(false)
  const [repeatType, setRepeatType] = useState<RepeatType>('daily')
  const [repeatInterval, setRepeatInterval] = useState(1)
  const [repeatWeekDays, setRepeatWeekDays] = useState<number[]>([])

  // Alarm
  const [alarmEnabled, setAlarmEnabled] = useState(false)
  const [alarmMode, setAlarmMode] = useState<'time' | 'before'>('before')
  const [alarmTime, setAlarmTime] = useState('09:00')
  const [alarmMinutesBefore, setAlarmMinutesBefore] = useState(30)

  // Subtasks
  const [subtasks, setSubtasks] = useState<{ id: string; title: string; isCompleted: boolean; createdAt?: string }[]>([])
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null)
  const [editingSubtaskValue, setEditingSubtaskValue] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)
  const editSubtaskInputRef = useRef<HTMLInputElement>(null)

  // Edit mode menu
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  // Attachments (pending, before task creation)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set())
  const [selectedSharedGroupIds, setSelectedSharedGroupIds] = useState<Set<string>>(new Set())
  const initialGroupIdsRef = useRef<Set<number>>(new Set())
  const fileInputRef = useRef<HTMLInputElement>(null)
  // 사용자가 알람 토글을 직접 만졌는지 추적 — dueTime 자동 알람 활성화 덮어쓰기 방지
  const hasUserToggledAlarmRef = useRef<boolean>(false)

  // Reschedule nudge
  const [showNudge, setShowNudge] = useState(false)

  const isEditMode = !!editTask
  const [showMemoLink, setShowMemoLink] = useState(false)
  const [pendingLinkedMemos, setPendingLinkedMemos] = useState<import('@/lib/types').LinkedMemoRef[]>([])

  // dueDate wrapper: 마감일 제거 시 알람·시간도 비활성
  const handleDueDateChange = (value: string) => {
    setDueDate(value)
    if (!value) {
      setAlarmEnabled(false)
      setDueTime('')
    }
  }

  // dueTime wrapper
  // - 시간 설정 시: 사용자가 알람을 직접 만지지 않았다면 "마감 시간에 알림" 자동 활성화
  // - 시간 제거 시: 자동 활성화된 알람은 함께 끄고, "0분 전" → "30분 전"으로 폴백
  const handleDueTimeChange = (value: string) => {
    setDueTime(value)
    if (value) {
      if (!hasUserToggledAlarmRef.current) {
        setAlarmEnabled(true)
        setAlarmMode('before')
        setAlarmMinutesBefore(0)
      }
    } else {
      if (alarmMinutesBefore === 0) {
        setAlarmMinutesBefore(30)
      }
      if (!hasUserToggledAlarmRef.current) {
        setAlarmEnabled(false)
      }
    }
  }

  // Natural language parse suggestions (create mode only)
  const nlHint = useMemo(() => {
    if (isEditMode || !title.trim()) return null
    const result = parseNaturalLanguage(title)
    if (!result.dueDate && !result.dueTime && !result.priority) return null
    return result
  }, [title, isEditMode])

  // Reactive isMobile detection
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Initialize form when modal opens
  useEffect(() => {
    if (!isOpen) return
    if (editTask) {
      setTitle(editTask.title)
      setMemo(editTask.memo || '')
      setCategoryId(editTask.categoryId)
      setDueDate(editTask.dueDate || '')
      setDueTime(editTask.dueTime || '')
      setPriority(editTask.priority)
      if (editTask.repeat.type !== 'none') {
        setRepeatEnabled(true)
        setRepeatType(editTask.repeat.type as RepeatType)
        setRepeatInterval(editTask.repeat.interval)
        setRepeatWeekDays(editTask.repeat.weekDays || [])
      } else {
        setRepeatEnabled(false)
        setRepeatType('daily')
        setRepeatInterval(1)
        setRepeatWeekDays([])
      }
      // Alarm
      setAlarmEnabled(editTask.alarm?.enabled || false)
      setAlarmMode(editTask.alarm?.time ? 'time' : 'before')
      setAlarmTime(editTask.alarm?.time || '09:00')
      setAlarmMinutesBefore(editTask.alarm?.minutesBefore ?? 30)
      // 편집 모드: 기존 알람 설정 존중 (dueTime 변경 시 자동 덮어쓰기 방지)
      hasUserToggledAlarmRef.current = true
      setSubtasks(editTask.subtasks?.map(st => ({
        id: st.id, title: st.title, isCompleted: st.isCompleted,
        createdAt: st.createdAt,
      })) || [])
      setNewSubtaskTitle('')
      setEditingSubtaskId(null)
      setEditingSubtaskValue('')
      setShowMoreMenu(false)
      setPendingFiles([])
      setFileError(null)
      if (editTask.id) {
        loadAttachments(editTask.id)
        const taskGroups = groups.filter(g => g.taskIds.includes(editTask.id!))
        const groupIdSet = new Set(taskGroups.map(g => g.id!))
        setSelectedGroupIds(groupIdSet)
        initialGroupIdsRef.current = new Set(groupIdSet)
        setSelectedSharedGroupIds(new Set())
      }
    } else {
      // Web Share Target 데이터 소비
      const sharedTitle = sessionStorage.getItem('share-target-title')
      const sharedMemo = sessionStorage.getItem('share-target-memo')
      setTitle(sharedTitle || '')
      setMemo(sharedMemo || '')
      if (sharedTitle) sessionStorage.removeItem('share-target-title')
      if (sharedMemo) sessionStorage.removeItem('share-target-memo')
      setCategoryId(null)
      setDueDate(defaultDueDate ?? '')
      setDueTime('')
      setPriority('none')
      setRepeatEnabled(false)
      setRepeatType('daily')
      setRepeatInterval(1)
      setRepeatWeekDays([])
      // Alarm
      setAlarmEnabled(false)
      setAlarmMode('before')
      setAlarmTime('09:00')
      setAlarmMinutesBefore(30)
      // 신규 작업: dueTime 설정 시 자동 알람 활성화 허용
      hasUserToggledAlarmRef.current = false
      setSubtasks([])
      setNewSubtaskTitle('')
      setEditingSubtaskId(null)
      setEditingSubtaskValue('')
      setShowMoreMenu(false)
      setPendingFiles([])
      setFileError(null)
      setSelectedGroupIds(new Set())
      setSelectedSharedGroupIds(new Set())
      initialGroupIdsRef.current = new Set()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editTask?.id])

  useEffect(() => {
    if (editingSubtaskId && editSubtaskInputRef.current) {
      editSubtaskInputRef.current.focus()
    }
  }, [editingSubtaskId])

  // Apply speech recognition transcript to title (only while actively listening)
  useEffect(() => {
    if (transcript && isListening) {
      setTitle(transcript)
      setTitleError(false)
    }
  }, [transcript, isListening])

  // iOS: autoFocus 대신 트랜지션 후 수동 포커스 (키보드 자동 열림 방지)
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => titleInputRef.current?.focus())
    }
  }, [isOpen])

  if (!isOpen) return null

  const resetForm = () => {
    setTitle('')
    setMemo('')
    setCategoryId(null)
    setDueDate(defaultDueDate ?? '')
    setDueTime('')
    setPriority('none')
    setRepeatEnabled(false)
    setRepeatType('daily')
    setRepeatInterval(1)
    setRepeatWeekDays([])
    setSubtasks([])
    setNewSubtaskTitle('')
    setEditingSubtaskId(null)
    setEditingSubtaskValue('')
    setShowMoreMenu(false)
    setPendingFiles([])
    setPendingLinkedMemos([])
    setFileError(null)
    setSelectedGroupIds(new Set())
    setSelectedSharedGroupIds(new Set())
    initialGroupIdsRef.current = new Set()
  }

  const toggleGroup = (groupId: number) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  const toggleSharedGroup = (groupId: string) => {
    setSelectedSharedGroupIds(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  // AI subtask decomposition
  const handleAiDecompose = async () => {
    if (!title.trim()) return
    setIsAiDecomposing(true)
    try {
      const { provider, apiKey } = getAIConfig()
      const result = await decomposeTaskWithAI(provider, apiKey, title.trim(), memo.trim() || undefined)
      setSubtasks(prev => [
        ...prev,
        ...result.map(st => ({
          id: crypto.randomUUID(),
          title: st.title,
          isCompleted: false,
        }))
      ])
    } catch (error) {
      useToastStore.getState().showToast(error instanceof Error ? error.message : 'AI 분해에 실패했습니다.', 'error')
    } finally {
      setIsAiDecomposing(false)
    }
  }

  // Subtask handlers
  const handleAddSubtask = () => {
    const trimmed = newSubtaskTitle.trim()
    if (!trimmed) return
    setSubtasks(prev => [...prev, {
      id: crypto.randomUUID(),
      title: trimmed,
      isCompleted: false,
    }])
    setNewSubtaskTitle('')
  }

  const handleToggleSubtask = (id: string) => {
    setSubtasks(prev => prev.map(st =>
      st.id === id ? { ...st, isCompleted: !st.isCompleted } : st
    ))
  }

  const handleDeleteSubtask = (id: string) => {
    setSubtasks(prev => prev.filter(st => st.id !== id))
    if (editingSubtaskId === id) setEditingSubtaskId(null)
  }

  const handleStartEditSubtask = (st: { id: string; title: string }) => {
    setEditingSubtaskId(st.id)
    setEditingSubtaskValue(st.title)
  }

  const handleSaveEditSubtask = () => {
    if (!editingSubtaskId) return
    const trimmed = editingSubtaskValue.trim()
    if (trimmed) {
      setSubtasks(prev => prev.map(st =>
        st.id === editingSubtaskId ? { ...st, title: trimmed } : st
      ))
    } else {
      setSubtasks(prev => prev.filter(st => st.id !== editingSubtaskId))
    }
    setEditingSubtaskId(null)
    setEditingSubtaskValue('')
  }

  // Edit mode action handlers
  const handleMarkComplete = async () => {
    if (!editTask?.id) return
    await toggleComplete(editTask.id)
    setShowMoreMenu(false)
    onClose()
  }

  const handleDuplicate = async () => {
    if (!editTask) return
    await addTask({
      title: editTask.title,
      memo: editTask.memo,
      categoryId: editTask.categoryId,
      status: 'pending',
      priority: editTask.priority,
      isFlagged: false,
      isStarred: false,
      dueDate: editTask.dueDate,
      dueTime: editTask.dueTime,
      alarm: editTask.alarm ? { ...editTask.alarm } : { enabled: false },
      repeat: editTask.repeat,
      subtasks: editTask.subtasks?.map(st => ({
        ...st, id: crypto.randomUUID(), isCompleted: false,
      })) || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sortOrder: 0,
    })
    setShowMoreMenu(false)
    onClose()
  }

  const handleDeleteTask = async () => {
    if (!editTask?.id) return
    await deleteTask(editTask.id)
    setShowMoreMenu(false)
    onClose()
  }

  const handleLinkMemo = (linkedMemo: import('@/lib/types').LinkedMemoRef) => {
    if (isEditMode && editTask?.id) {
      const existing = editTask.linkedMemos || []
      if (existing.some((m) => m.memoSyncId === linkedMemo.memoSyncId)) return
      import('@/stores/taskStore').then(({ useTaskStore: taskStore }) => {
        taskStore.getState().updateTask(editTask!.id!, { linkedMemos: [...existing, linkedMemo] })
      })
    } else {
      // 생성 모드: 로컬 상태에 추가
      setPendingLinkedMemos((prev) => {
        if (prev.some((m) => m.memoSyncId === linkedMemo.memoSyncId)) return prev
        return [...prev, linkedMemo]
      })
    }
  }

  const handleUnlinkMemo = (memoSyncId: string) => {
    if (!editTask?.id) return
    const updated = (editTask.linkedMemos || []).filter((m) => m.memoSyncId !== memoSyncId)
    import('@/stores/taskStore').then(({ useTaskStore: taskStore }) => {
      taskStore.getState().updateTask(editTask!.id!, { linkedMemos: updated })
    })
  }

  const handleSave = async () => {
    if (isSaving) return // 더블 클릭/탭 중복 제출 방지
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setTitleError(true)
      return
    }
    setTitleError(false)
    setIsSaving(true)
    try {

    // Alarm 객체 빌드
    const alarmObj: TaskAlarm = alarmEnabled && dueDate
      ? {
          enabled: true,
          ...(alarmMode === 'time' ? { time: alarmTime } : { minutesBefore: alarmMinutesBefore }),
        }
      : { enabled: false }

    // 입력 중인 하위 작업 자동 추가
    const pendingSubtask = newSubtaskTitle.trim()
    const finalSubtasks = pendingSubtask
      ? [...subtasks, { id: crypto.randomUUID(), title: pendingSubtask, isCompleted: false }]
      : subtasks

    if (editTask?.id) {
      // Edit mode
      await updateTask(editTask.id, {
        title: trimmedTitle,
        memo: memo.trim() || undefined,
        categoryId,
        priority,
        dueDate: dueDate || undefined,
        dueTime: (dueDate && dueTime) ? dueTime : undefined,
        alarm: alarmObj,
        repeat: repeatEnabled
          ? { type: repeatType, interval: repeatInterval, ...(repeatType === 'daily' && repeatWeekDays.length > 0 ? { weekDays: repeatWeekDays } : {}), ...((repeatType === 'monthly' || repeatType === 'yearly') && dueDate ? { monthDay: Number(dueDate.slice(8, 10)) } : {}) }
          : { type: 'none' as const, interval: 1 },
        subtasks: finalSubtasks.map((st, i) => ({
          id: st.id,
          title: st.title,
          isCompleted: st.isCompleted,
          sortOrder: i,
          createdAt: st.createdAt || new Date().toISOString(),
        })),
      })

      if (pendingFiles.length > 0) {
        for (const pf of pendingFiles) {
          await addAttachment(editTask.id, pf.file)
        }
      }

      // Group changes (diff)
      for (const gid of selectedGroupIds) {
        if (!initialGroupIdsRef.current.has(gid)) {
          await addTaskToGroup(gid, editTask.id)
        }
      }
      for (const gid of initialGroupIdsRef.current) {
        if (!selectedGroupIds.has(gid)) {
          await removeTaskFromGroup(gid, editTask.id)
        }
      }
    } else {
      // Create mode
      const isBulk = !!defaultDueDates && defaultDueDates.length > 1

      // 일괄/단일 공통으로 사용할 작업 페이로드(날짜 제외) 1회 빌드
      // 일괄 모드에서는 작업마다 하위 작업 id를 새로 발급해 충돌을 방지
      const buildPayload = (targetDate: string | undefined, freshSubtaskIds: boolean) => ({
        title: trimmedTitle,
        memo: memo.trim() || undefined,
        categoryId,
        status: 'pending' as const,
        priority,
        isFlagged: false,
        isStarred: false,
        dueDate: targetDate || undefined,
        dueTime: (targetDate && dueTime) ? dueTime : undefined,
        alarm: (alarmObj.enabled && targetDate) ? alarmObj : { enabled: false } as TaskAlarm,
        repeat: repeatEnabled
          ? { type: repeatType, interval: repeatInterval, ...(repeatType === 'daily' && repeatWeekDays.length > 0 ? { weekDays: repeatWeekDays } : {}), ...((repeatType === 'monthly' || repeatType === 'yearly') && targetDate ? { monthDay: Number(targetDate.slice(8, 10)) } : {}) }
          : { type: 'none' as const, interval: 1 },
        subtasks: finalSubtasks.map((st, i) => ({
          id: freshSubtaskIds ? crypto.randomUUID() : st.id,
          title: st.title,
          isCompleted: st.isCompleted,
          sortOrder: i,
          createdAt: new Date().toISOString(),
        })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sortOrder: 0,
        linkedMemos: pendingLinkedMemos.length > 0 ? pendingLinkedMemos : undefined,
      })

      if (isBulk) {
        // 일괄 작업 생성: 선택된 각 날짜마다 동일한 작업을 1개씩 생성
        for (const d of defaultDueDates!) {
          await addTask(buildPayload(d, true))
        }
        useToastStore.getState().showToast(`${defaultDueDates!.length}개 날짜에 작업을 만들었습니다.`, 'success')
      } else {
        const taskId = await addTask(buildPayload(dueDate, false))

        if (taskId && pendingFiles.length > 0) {
          for (const pf of pendingFiles) {
            await addAttachment(taskId, pf.file)
          }
        }

        // Add to selected personal groups
        if (taskId && selectedGroupIds.size > 0) {
          for (const gid of selectedGroupIds) {
            await addTaskToGroup(gid, taskId)
          }
        }

        // Add to selected shared groups
        if (selectedSharedGroupIds.size > 0) {
          for (const sgid of selectedSharedGroupIds) {
            try {
              await addSharedTask(sgid, {
                title: trimmedTitle,
                description: memo.trim() || '',
                priority: priority,
                dueDate: dueDate || undefined,
              })
            } catch {
              // Shared group task creation failed silently
            }
          }
        }
      }
    }

    // Cleanup preview URLs
    pendingFiles.forEach((pf) => {
      if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl)
    })

    // Check for reschedule nudge (edit mode, dueDate pushed)
    if (editTask?.id) {
      const updated = useTaskStore.getState().tasks.find(t => t.id === editTask.id)
      if (updated && (updated.rescheduleCount || 0) >= 3) {
        setShowNudge(true)
        return
      }
    }

    resetForm()
    onClose()
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') {
      onClose()
    }
  }

  const handleTemplateSelect = (template: {
    title: string
    categoryId: number | null
    priority: TaskPriority
    subtasks: { title: string; sortOrder: number }[]
    repeat: { type: string; interval: number }
    memo?: string
  }) => {
    setTitle(template.title)
    if (template.memo) setMemo(template.memo)
    setCategoryId(template.categoryId)
    setPriority(template.priority)
    if (template.repeat.type !== 'none') {
      setRepeatEnabled(true)
      setRepeatType(template.repeat.type as RepeatType)
      setRepeatInterval(template.repeat.interval)
    }
    setShowTemplatePicker(false)
  }

  const handleAiParse = async () => {
    const trimmed = title.trim()
    if (!trimmed) return

    setIsAiParsing(true)
    try {
      const { provider, apiKey } = getAIConfig()
      const categoryNames = categories.map((c) => c.name)
      const parsed = await parseTaskWithAI(provider, apiKey, trimmed, categoryNames)

      setTitle(parsed.title)
      if (parsed.priority) setPriority(parsed.priority)
      if (parsed.dueDate) setDueDate(parsed.dueDate)
      if (parsed.categoryHint) {
        const matchedCat = categories.find((c) => c.name === parsed.categoryHint)
        if (matchedCat?.id) setCategoryId(matchedCat.id)
      }
    } catch (error) {
      console.error('AI parse failed:', error)
    } finally {
      setIsAiParsing(false)
    }
  }

  // Attachment handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    setFileError(null)

    for (const file of Array.from(files)) {
      if (pendingFiles.length >= 5) {
        setFileError('최대 5개까지 첨부할 수 있습니다.')
        break
      }
      // M3: File extension validation
      if (!isAllowedFile(file.name)) {
        setFileError('허용되지 않는 파일 형식입니다. (이미지, PDF, 문서만 가능)')
        continue
      }
      if (file.size > 5 * 1024 * 1024) {
        setFileError('파일 크기는 5MB를 초과할 수 없습니다.')
        continue
      }

      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined

      setPendingFiles((prev) => [
        ...prev,
        { id: crypto.randomUUID(), file, previewUrl },
      ])
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveFile = (id: string) => {
    setPendingFiles((prev) => {
      const target = prev.find((f) => f.id === id)
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((f) => f.id !== id)
    })
  }

  return (
    <div className="fixed inset-0 z-50 sm:flex sm:items-center sm:justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal — 모바일: 전체화면 fixed, sm+: 중앙 카드 */}
      <div className="fixed inset-0 z-[60] sm:relative sm:inset-auto sm:z-auto sm:w-full sm:max-w-md sm:max-h-[85dvh] bg-surface-primary sm:rounded-2xl border-none sm:border sm:border-base shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 fold:px-3 fold:py-2 border-b border-base flex-shrink-0" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
          <h2 className="text-lg fold:text-base font-semibold text-heading">
            {isEditMode ? '작업 수정' : '새 작업'}
          </h2>
          <div className="flex items-center gap-1">
            {!isEditMode && aiEnabled && (
              <button
                type="button"
                onClick={handleAiParse}
                disabled={isAiParsing || !title.trim()}
                className="p-1.5 rounded-lg text-disabled hover:text-purple-500 hover-surface transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="AI로 자동 분석"
              >
                {isAiParsing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
              </button>
            )}
            {!isEditMode && (
              <button
                type="button"
                onClick={() => setShowTemplatePicker(true)}
                className="p-1.5 rounded-lg text-disabled hover:text-primary-500 hover-surface transition-colors"
                title="템플릿에서 만들기"
              >
                <LayoutTemplate className="w-5 h-5" />
              </button>
            )}
            {isEditMode && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="p-1.5 rounded-lg text-disabled hover:text-[var(--text-secondary)] hover-surface transition-colors"
                  title="더보기"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {showMoreMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 w-44 bg-surface-primary rounded-xl border border-strong shadow-lg z-20 py-1">
                      <button
                        type="button"
                        onClick={handleMarkComplete}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover-surface text-heading transition-colors"
                      >
                        <Check className="w-4 h-4" />
                        {editTask?.status === 'completed' ? '미완료로 변경' : '완료로 표시'}
                      </button>
                      <button
                        type="button"
                        onClick={handleDuplicate}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover-surface text-heading transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        복사
                      </button>
                      <div className="border-t border-base my-1" />
                      <button
                        type="button"
                        onClick={handleDeleteTask}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        삭제
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-disabled hover:text-[var(--text-secondary)] hover-surface transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto p-4 fold:p-3 space-y-4 fold:space-y-3 min-h-0">
          {/* Title */}
          <div>
            <label htmlFor="task-title-input" className="sr-only">작업 제목</label>
            <div className="relative">
              <input
                id="task-title-input"
                ref={titleInputRef}
                type="text"
                value={title}
                onChange={(e) => {
                  const v = e.target.value
                  if (v.length <= TITLE_MAX_LENGTH) {
                    setTitle(v)
                    if (titleError) setTitleError(false)
                  }
                }}
                maxLength={TITLE_MAX_LENGTH}
                onKeyDown={handleKeyDown}
                placeholder="자연어로 입력 (예: 내일까지 보고서 작성 긴급)"
                aria-invalid={titleError}
                aria-describedby={titleError ? 'title-error' : undefined}
                className={clsx(
                  'w-full px-3 py-2.5 text-sm rounded-lg border bg-surface-secondary text-heading placeholder:text-disabled outline-none focus:ring-1 transition-colors',
                  isSupported && voiceEnabled ? 'pr-10' : '',
                  titleError
                    ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
                    : 'border-strong focus:border-primary-500 focus:ring-primary-500'
                )}
              />
              {voiceEnabled && (
                isSupported ? (
                  <button
                    type="button"
                    onClick={isListening ? stopListening : startListening}
                    className={clsx(
                      'absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors',
                      isListening
                        ? 'text-danger-500 animate-pulse'
                        : 'text-disabled hover:text-[var(--text-secondary)] hover:bg-[var(--active-bg)]'
                    )}
                    aria-label={isListening ? '음성 입력 중지' : '음성으로 입력'}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                ) : (
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-disabled"
                    title="키보드의 마이크 버튼을 사용하세요"
                  >
                    <Mic className="w-4 h-4 opacity-30" />
                  </span>
                )
              )}
            </div>
            {titleError && (
              <p id="title-error" role="alert" className="mt-1 text-xs text-danger-500">제목을 입력해 주세요.</p>
            )}
            {title.length > TITLE_MAX_LENGTH * 0.8 && (
              <p className={`mt-1 text-xs ${title.length >= TITLE_MAX_LENGTH ? 'text-danger-500' : 'text-caption'}`}>
                {title.length}/{TITLE_MAX_LENGTH}
              </p>
            )}
          </div>

          {/* NL parse hint */}
          {nlHint && (
            <div className="flex items-center gap-2 flex-wrap">
              <NlHintChips parsed={nlHint} />
              <button
                type="button"
                onClick={() => {
                  if (nlHint.cleanTitle) setTitle(nlHint.cleanTitle)
                  if (nlHint.dueDate) handleDueDateChange(nlHint.dueDate)
                  if (nlHint.dueTime) handleDueTimeChange(nlHint.dueTime)
                  if (nlHint.priority) setPriority(nlHint.priority)
                }}
                className="text-xs text-primary-500 hover:text-primary-600 font-medium"
              >
                적용
              </button>
            </div>
          )}

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-caption">
                하위 작업
                {subtasks.length > 0 && (
                  <span className="ml-1 text-disabled">{subtasks.filter(st => st.isCompleted).length}/{subtasks.length}</span>
                )}
              </label>
              {aiEnabled && title.trim() && (
                <button
                  type="button"
                  onClick={handleAiDecompose}
                  disabled={isAiDecomposing}
                  className="flex items-center gap-1 px-2 py-0.5 text-meta font-medium text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors disabled:opacity-50"
                >
                  {isAiDecomposing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  AI 분해
                </button>
              )}
            </div>

            {subtasks.length > 0 && (
              <div className="space-y-1 mb-2">
                {subtasks.map(st => (
                  <div key={st.id} className="flex items-center gap-2 group">
                    <button
                      type="button"
                      onClick={() => handleToggleSubtask(st.id)}
                      className={clsx(
                        "w-4 h-4 rounded-full border-2 flex-shrink-0 transition-colors",
                        st.isCompleted
                          ? "bg-primary-500 border-primary-500"
                          : "border-strong"
                      )}
                    >
                      {st.isCompleted && <Check className="w-3 h-3 text-white m-auto" />}
                    </button>
                    {editingSubtaskId === st.id ? (
                      <input
                        ref={editSubtaskInputRef}
                        type="text"
                        value={editingSubtaskValue}
                        onChange={(e) => setEditingSubtaskValue(e.target.value)}
                        onBlur={handleSaveEditSubtask}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            e.stopPropagation()
                            handleSaveEditSubtask()
                          } else if (e.key === 'Escape') {
                            setEditingSubtaskId(null)
                            setEditingSubtaskValue('')
                          } else if (e.key === 'Backspace' && editingSubtaskValue === '') {
                            handleDeleteSubtask(st.id)
                          }
                        }}
                        className="flex-1 text-sm bg-transparent border-none outline-none text-heading placeholder:text-disabled"
                        placeholder="하위 작업 입력"
                      />
                    ) : (
                      <span
                        onClick={() => handleStartEditSubtask(st)}
                        className={clsx(
                          "flex-1 text-sm cursor-text",
                          st.isCompleted
                            ? "line-through text-disabled"
                            : "text-heading"
                        )}
                      >
                        {st.title}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteSubtask(st.id)}
                      className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-0.5 text-disabled hover:text-red-500 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAddSubtask}
                className="p-0.5 text-primary-500 hover:text-primary-600 flex-shrink-0 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
              <input
                type="text"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleAddSubtask()
                  }
                }}
                placeholder="하위 작업 추가 (Enter 또는 + 버튼)"
                className="flex-1 text-sm py-1.5 bg-transparent text-heading placeholder:text-disabled outline-none border-b border-strong focus:border-primary-500 transition-colors"
              />
            </div>
          </div>

          {/* Memo */}
          <div>
            <label className="block text-xs font-medium text-caption mb-1.5">
              메모
            </label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="메모를 입력하세요 (선택사항)"
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-strong bg-surface-secondary text-heading placeholder:text-disabled outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-caption mb-1.5">
              카테고리
            </label>
            <div className="overflow-x-auto scrollbar-none -mx-1 px-1">
              <div className="flex items-center gap-1.5 pb-1">
                <button
                  type="button"
                  onClick={() => setCategoryId(null)}
                  className={clsx(
                    'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
                    categoryId === null
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface-tertiary text-body'
                  )}
                >
                  미분류
                </button>
                {categories
                  .filter((c) => !c.parentId)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .flatMap((root) => {
                    const children = categories
                      .filter((c) => c.parentId === root.id)
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                    return [root, ...children]
                  })
                  .map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategoryId(cat.id ?? null)}
                      className={clsx(
                        'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1',
                        categoryId === cat.id
                          ? 'bg-primary-500 text-white'
                          : 'bg-surface-tertiary text-body'
                      )}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getCategoryColorStyle(cat.color) }}
                      />
                      {cat.parentId && <span className="text-meta opacity-60">└</span>}
                      {cat.name}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Group */}
          {(groups.length > 0 || (isLoggedIn && sharedGroups.length > 0)) && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <FolderKanban className="w-4 h-4 text-caption" />
                <label className="text-xs font-medium text-caption">
                  그룹
                </label>
                {(selectedGroupIds.size + selectedSharedGroupIds.size) > 0 && (
                  <span className="text-meta text-primary-500 font-medium">{selectedGroupIds.size + selectedSharedGroupIds.size}개</span>
                )}
              </div>

              {/* 개인 그룹 */}
              {groups.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center gap-1 mb-1">
                    <FolderKanban className="w-3 h-3 text-disabled" />
                    <span className="text-meta font-medium text-disabled uppercase tracking-wider">개인 그룹</span>
                  </div>
                  <div className="overflow-x-auto scrollbar-none -mx-1 px-1">
                    <div className="flex items-center gap-1.5 pb-1">
                      {groups.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => toggleGroup(g.id!)}
                          className={clsx(
                            'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5',
                            selectedGroupIds.has(g.id!)
                              ? 'bg-primary-500 text-white'
                              : 'bg-surface-tertiary text-body'
                          )}
                        >
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selectedGroupIds.has(g.id!) ? '#fff' : g.color }}
                          />
                          {g.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 공유 그룹 */}
              {isLoggedIn && sharedGroups.length > 0 && (
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Users className="w-3 h-3 text-disabled" />
                    <span className="text-meta font-medium text-disabled uppercase tracking-wider">공유 그룹</span>
                  </div>
                  <div className="overflow-x-auto scrollbar-none -mx-1 px-1">
                    <div className="flex items-center gap-1.5 pb-1">
                      {sharedGroups.map((sg) => (
                        <button
                          key={sg.id}
                          type="button"
                          onClick={() => toggleSharedGroup(sg.id)}
                          className={clsx(
                            'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5',
                            selectedSharedGroupIds.has(sg.id)
                              ? 'bg-primary-500 text-white'
                              : 'bg-surface-tertiary text-body'
                          )}
                        >
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selectedSharedGroupIds.has(sg.id) ? '#fff' : sg.color }}
                          />
                          {sg.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Due Date */}
          <div>
            <label className="block text-xs font-medium text-caption mb-1.5">
              마감일
            </label>
            <div className="px-3 py-2 rounded-lg border border-strong bg-surface-secondary">
              {isMobile ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(true)}
                    className="flex items-center gap-2 text-sm text-heading"
                  >
                    <span>{dueDate ? new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' }).format(new Date(dueDate + 'T00:00')) : '날짜를 선택하세요'}</span>
                  </button>
                  <DatePickerBottomSheet
                    isOpen={showDatePicker}
                    onClose={() => setShowDatePicker(false)}
                    value={dueDate || undefined}
                    onChange={handleDueDateChange}
                  />
                </>
              ) : (
                <DatePicker
                  value={dueDate || undefined}
                  onChange={handleDueDateChange}
                  placeholder="날짜를 선택하세요"
                />
              )}
            </div>
            {/* Due Time — shown only when dueDate is set */}
            {dueDate && (
              <div className="flex items-center gap-2 mt-2">
                <label className="text-xs font-medium text-caption whitespace-nowrap">마감 시간</label>
                <input
                  type="time"
                  value={dueTime}
                  onChange={(e) => handleDueTimeChange(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-strong bg-surface-secondary text-heading outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                />
                {dueTime && (
                  <button
                    type="button"
                    onClick={() => handleDueTimeChange('')}
                    className="p-1 text-disabled hover:text-red-500 transition-colors"
                    title="시간 제거"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            {dueTime && alarmEnabled && alarmMode === 'before' && alarmMinutesBefore === 0 && (
              <p className="flex items-center gap-1 mt-1.5 text-meta text-primary-500 dark:text-primary-400">
                <Bell className="w-3 h-3 flex-shrink-0" />
                <span>마감 시간 {dueTime}에 알림을 받습니다</span>
              </p>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-xs font-medium text-caption mb-1.5">
              우선순위
            </label>
            <div className="flex gap-2 fold:grid fold:grid-cols-2">
              {priorityOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPriority(opt.value)}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${priority === opt.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                    : 'border-strong text-body hover-surface'
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-base" />

          {/* Repeat Task */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-caption" />
                <label className="text-xs font-medium text-caption">
                  반복 작업으로 설정
                </label>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={repeatEnabled}
                onClick={() => setRepeatEnabled(!repeatEnabled)}
                className={clsx(
                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  repeatEnabled ? 'bg-primary-500' : 'bg-surface-muted'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    repeatEnabled ? 'translate-x-4' : 'translate-x-0'
                  )}
                />
              </button>
            </div>

            {repeatEnabled && (
              <div className="space-y-3 mt-2">
                <div className="flex gap-2">
                  {repeatOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setRepeatType(opt.value)}
                      className={`flex-1 px-2 py-1.5 text-xs rounded-lg border transition-colors ${repeatType === opt.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                        : 'border-strong text-body hover-surface'
                        }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Weekday selector — only for daily repeat */}
                {repeatType === 'daily' && (
                  <div>
                    <span className="text-xs text-caption">요일 선택 (선택 시 해당 요일만 반복):</span>
                    <div className="flex gap-1 mt-1">
                      {(['일', '월', '화', '수', '목', '금', '토'] as const).map((label, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setRepeatWeekDays(prev =>
                              prev.includes(idx)
                                ? prev.filter(d => d !== idx)
                                : [...prev, idx].sort()
                            )
                          }}
                          className={clsx(
                            'w-8 h-8 text-xs rounded-full border transition-colors font-medium',
                            repeatWeekDays.includes(idx)
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                              : 'border-strong text-body hover-surface',
                            idx === 0 && repeatWeekDays.includes(idx) && 'text-red-500 border-red-400 bg-red-50 dark:bg-red-900/20',
                            idx === 6 && repeatWeekDays.includes(idx) && 'text-blue-500 border-blue-400 bg-blue-50 dark:bg-blue-900/20',
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Interval */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-caption">반복 간격:</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setRepeatInterval(Math.max(1, repeatInterval - 1))}
                      className="w-7 h-7 rounded-lg border border-strong text-body text-sm flex items-center justify-center hover-surface"
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-sm font-medium text-heading">
                      {repeatInterval}
                    </span>
                    <button
                      type="button"
                      onClick={() => setRepeatInterval(Math.min(30, repeatInterval + 1))}
                      className="w-7 h-7 rounded-lg border border-strong text-body text-sm flex items-center justify-center hover-surface"
                    >
                      +
                    </button>
                  </div>
                  <span className="text-xs text-disabled">
                    {repeatType === 'daily' && (repeatInterval === 1 ? '매일' : `${repeatInterval}일마다`)}
                    {repeatType === 'weekly' && (repeatInterval === 1 ? '매주' : `${repeatInterval}주마다`)}
                    {repeatType === 'monthly' && (repeatInterval === 1 ? '매월' : `${repeatInterval}개월마다`)}
                    {repeatType === 'yearly' && (repeatInterval === 1 ? '매년' : `${repeatInterval}년마다`)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Alarm */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-caption" />
                <label className="text-xs font-medium text-caption">
                  알람 설정
                </label>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={alarmEnabled}
                onClick={() => {
                  if (!dueDate && !alarmEnabled) return
                  hasUserToggledAlarmRef.current = true
                  setAlarmEnabled(!alarmEnabled)
                }}
                disabled={!dueDate}
                className={clsx(
                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  !dueDate ? 'opacity-40 cursor-not-allowed' : '',
                  alarmEnabled ? 'bg-primary-500' : 'bg-surface-muted'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    alarmEnabled ? 'translate-x-4' : 'translate-x-0'
                  )}
                />
              </button>
            </div>

            {!dueDate && (
              <p className="text-meta text-disabled">마감일을 먼저 설정하세요</p>
            )}

            {alarmEnabled && dueDate && (
              <div className="space-y-3 mt-2">
                {/* Mode selection */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAlarmMode('before')}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-lg border transition-colors ${alarmMode === 'before'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                      : 'border-strong text-body hover-surface'
                    }`}
                  >
                    마감 전
                  </button>
                  <button
                    type="button"
                    onClick={() => setAlarmMode('time')}
                    className={`flex-1 px-2 py-1.5 text-xs rounded-lg border transition-colors ${alarmMode === 'time'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                      : 'border-strong text-body hover-surface'
                    }`}
                  >
                    정확한 시간
                  </button>
                </div>

                {alarmMode === 'before' ? (
                  <div>
                    <div className="flex flex-wrap gap-1.5">
                      {alarmBeforeOptions
                        .filter(opt => opt.value !== 0 || dueTime)
                        .map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setAlarmMinutesBefore(opt.value)}
                          className={clsx(
                            'px-2.5 py-1 text-xs rounded-full border transition-colors',
                            alarmMinutesBefore === opt.value
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                              : 'border-strong text-body hover-surface'
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {dueTime && (
                      <p className="text-meta text-caption mt-1.5">
                        {alarmMinutesBefore === 0
                          ? `마감 시간 ${dueTime}에 알림`
                          : `마감 시간 ${dueTime} 기준 ${alarmBeforeOptions.find(o => o.value === alarmMinutesBefore)?.label || `${alarmMinutesBefore}분 전`}에 알림`
                        }
                      </p>
                    )}
                    {!dueTime && (
                      <p className="text-meta text-disabled mt-1.5">
                        마감일 오전 09:00 기준
                      </p>
                    )}
                  </div>
                ) : (
                  <input
                    type="time"
                    value={alarmTime}
                    onChange={(e) => setAlarmTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-strong bg-surface-secondary text-heading outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                  />
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-base" />

          {/* Attachments */}
          {/* 연결된 메모 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-caption" />
                <label className="text-xs font-medium text-caption">연결된 메모</label>
              </div>
              <button
                type="button"
                onClick={() => setShowMemoLink(true)}
                className="text-meta text-primary-500 hover:text-primary-600"
              >
                + 연결
              </button>
            </div>
            {(isEditMode ? editTask?.linkedMemos : pendingLinkedMemos)?.map((linkedMemo) => (
              <div
                key={linkedMemo.memoSyncId}
                className="flex items-center gap-2 p-2 rounded-lg border border-base border-l-4 mb-1.5"
                style={{ borderLeftColor: linkedMemo.memoColor }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-heading truncate">{linkedMemo.memoTitle}</p>
                  {linkedMemo.memoPreview && <p className="text-meta text-caption truncate">{linkedMemo.memoPreview}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => isEditMode ? handleUnlinkMemo(linkedMemo.memoSyncId) : setPendingLinkedMemos((prev) => prev.filter((m) => m.memoSyncId !== linkedMemo.memoSyncId))}
                  className="p-1 text-disabled hover:text-red-500 flex-shrink-0"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-caption" />
                <label className="text-xs font-medium text-caption">
                  첨부 파일
                </label>
                {(existingAttachments.length + pendingFiles.length) > 0 && (
                  <span className="text-meta text-disabled">
                    {existingAttachments.length + pendingFiles.length}/5
                  </span>
                )}
              </div>
              {(existingAttachments.length + pendingFiles.length) < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                >
                  <Plus className="w-4 h-4" />
                  추가
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,text/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {fileError && (
              <p className="text-xs text-red-500 mb-2">{fileError}</p>
            )}

            {/* Existing attachments (edit mode) */}
            {isEditMode && existingAttachments.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {existingAttachments.filter((att) => att.id != null).map((att) => (
                  <ExistingAttachmentItem
                    key={att.id}
                    attachment={att}
                    onDelete={() => deleteExistingAttachment(att.id as number, editTask!.id!)}
                  />
                ))}
              </div>
            )}

            {/* New pending files */}
            {pendingFiles.length > 0 && (
              <div className="space-y-1.5">
                {pendingFiles.map((pf) => (
                  <div
                    key={pf.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-surface-secondary group"
                  >
                    {pf.previewUrl ? (
                      <img
                        src={pf.previewUrl}
                        alt={pf.file.name}
                        className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-surface-muted flex items-center justify-center flex-shrink-0">
                        {pf.file.type.startsWith('image/') ? (
                          <ImageIcon className="w-4 h-4 text-disabled" />
                        ) : (
                          <FileText className="w-4 h-4 text-disabled" />
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-heading truncate">
                        {pf.file.name}
                      </p>
                      <p className="text-meta text-disabled">{formatFileSize(pf.file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(pf.id)}
                      className="p-1 rounded text-disabled hover:text-red-500 hover:bg-[var(--active-bg)] transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-4 py-3 fold:px-3 fold:py-2 border-t border-base flex-shrink-0 bg-surface-primary"
          style={{ paddingBottom: kbOffset > 0 ? kbOffset : 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
        >
          <Button variant="ghost" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!title.trim() || isSaving}
            leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
          >
            {isSaving ? '저장 중...' : isEditMode ? '수정' : '저장'}
          </Button>
        </div>
      </div>

      {/* Template picker modal */}
      {showTemplatePicker && (
        <TemplatePickerModal
          isOpen={showTemplatePicker}
          onClose={() => setShowTemplatePicker(false)}
          onSelect={handleTemplateSelect}
        />
      )}

      {/* Reschedule nudge modal */}
      <RescheduleNudgeModal
        isOpen={showNudge}
        onClose={() => { setShowNudge(false); resetForm(); onClose() }}
        task={editTask || null}
        onSplit={() => { resetForm(); onClose() }}
        onFiveMin={() => {
          if (editTask?.id) {
            useFocusStore.getState().startFocus(editTask.id, editTask.title)
          }
          resetForm(); onClose()
        }}
        onDelegate={() => { resetForm(); onClose() }}
        onDelete={() => {
          if (editTask?.id) deleteTask(editTask.id)
          resetForm(); onClose()
        }}
      />

      {/* 메모 연결 모달 */}
      <MemoLinkModal
        isOpen={showMemoLink}
        onClose={() => setShowMemoLink(false)}
        onSelect={handleLinkMemo}
        excludeIds={[
          ...(editTask?.linkedMemos?.map((m) => m.memoSyncId) || []),
          ...pendingLinkedMemos.map((m) => m.memoSyncId),
        ]}
      />
    </div>
  )
}
